import type { Conversation, Message, Model, Provider, Image, Job, Reaction, ClientConfig } from '../state/types';
import { AuthService } from './auth';

export class ApiService {
    private baseUrl: string;
    private auth: AuthService;

    constructor(baseUrl: string = window.location.origin) {
        this.baseUrl = baseUrl;
        this.auth = AuthService.getInstance();
    }

    private async getAuthHeaders(): Promise<HeadersInit> {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
        };

        const token = await this.auth.getToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return headers;
    }

    private getFullUrl(path: string): string {
        return path.startsWith('http') ? path : `${this.baseUrl}${path}`;
    }

    private encodePathId(id: string): string {
        return btoa(id)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
    }

    async get<T>(path: string): Promise<T> {
        const response = await fetch(this.getFullUrl(path), {
            headers: await this.getAuthHeaders(),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
    }

    async post<T>(path: string, body: unknown): Promise<T> {
        const response = await fetch(this.getFullUrl(path), {
            method: 'POST',
            headers: await this.getAuthHeaders(),
            body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
    }

    async patch<T>(path: string, body: unknown): Promise<T> {
        const response = await fetch(this.getFullUrl(path), {
            method: 'PATCH',
            headers: await this.getAuthHeaders(),
            body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
    }

    async patchNoResponse(path: string, body: unknown): Promise<void> {
        const response = await fetch(this.getFullUrl(path), {
            method: 'PATCH',
            headers: await this.getAuthHeaders(),
            body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
    }

    async delete(path: string): Promise<void> {
        const response = await fetch(this.getFullUrl(path), {
            method: 'DELETE',
            headers: await this.getAuthHeaders(),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
    }

    async getStream(path: string): Promise<Response> {
        const response = await fetch(this.getFullUrl(path), {
            headers: await this.getAuthHeaders(),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response;
    }

    async postStream(path: string, body: unknown, signal?: AbortSignal): Promise<Response> {
        const response = await fetch(this.getFullUrl(path), {
            method: 'POST',
            headers: await this.getAuthHeaders(),
            body: JSON.stringify(body),
            signal,
        });
        if (!response.ok) {
            const errorBody = await response.text();
            console.error(`HTTP ${response.status} error response:`, errorBody);
            throw new Error(`HTTP ${response.status}: ${errorBody}`);
        }
        return response;
    }

    async createConversation(model: string): Promise<Conversation> {
        return this.post('/api/conversations', { model });
    }

    async getConversation(id: string): Promise<Conversation> {
        return this.get(`/api/conversations/${id}`);
    }

    async listConversations(): Promise<Conversation[]> {
        return this.get('/api/conversations');
    }

    async deleteConversation(id: string): Promise<void> {
        await this.delete(`/api/conversations/${id}`);
    }

    async updateConversationModel(id: string, model: string): Promise<Conversation> {
        return this.patch(`/api/conversations/${id}`, { model });
    }

    async getMessages(conversationId: string): Promise<Message[]> {
        return this.get(`/api/conversations/${conversationId}/messages`);
    }

    async createMessage(conversationId: string, role: string, content: string, images?: Image[]): Promise<Message> {
        return this.post(`/api/conversations/${conversationId}/messages`, {
            role,
            content,
            images,
        });
    }

    async updateMessage(messageId: string, content: string): Promise<Message> {
        return this.patch(`/api/messages/${messageId}`, { content });
    }

    async updateActionSteps(messageId: string, actionSteps: unknown[]): Promise<void> {
        await this.patchNoResponse(`/api/messages/${messageId}/action-steps`, { action_steps: actionSteps });
    }

    async updateMessageMetadata(messageId: string, data: { reactions?: Reaction[], flags?: string[] }): Promise<void> {
        await this.patchNoResponse(`/api/messages/${messageId}/metadata`, data);
    }

    async deleteMessage(messageId: string): Promise<void> {
        await this.delete(`/api/messages/${messageId}`);
    }

    async chatStream(conversationId: string, content: string, model: string, images?: Image[], signal?: AbortSignal): Promise<Response> {
        return this.postStream(`/api/conversations/${conversationId}/chat`, {
            role: 'user',
            content,
            model,
            images,
        }, signal);
    }

    async stopStream(conversationId: string): Promise<void> {
        await this.post(`/api/conversations/${conversationId}/stop`, {});
    }

    async resumeStream(conversationId: string): Promise<Response> {
        return this.getStream(`/api/conversations/${conversationId}/stream`);
    }

    async tryAgain(messageId: string, conversationId: string, model?: string, signal?: AbortSignal): Promise<Response> {
        return this.postStream(`/api/messages/${messageId}/try-again`, { 
            model,
            conversation_id: conversationId 
        }, signal);
    }

    async listModels(): Promise<Model[]> {
        return this.get('/api/models');
    }

    async getConfig(): Promise<ClientConfig> {
        return this.get('/api/config');
    }

    async getJobStatus(conversationId: string, jobId: string): Promise<Job> {
        return this.get(`/api/conversations/${conversationId}/jobs/${jobId}`);
    }

    async listJobs(conversationId: string, status?: string): Promise<Job[]> {
        const query = status ? `?status=${status}` : '';
        return this.get(`/api/conversations/${conversationId}/jobs${query}`);
    }

    async cancelJob(conversationId: string, jobId: string): Promise<{ status: string }> {
        return this.post(`/api/conversations/${conversationId}/jobs/${jobId}/cancel`, {});
    }

    // Model settings
    async createModel(model: {
        id: string;
        display_name: string;
        provider_id: string;
        vision_capable: boolean;
        temperature: number;
        max_tokens: number;
        system_prompt: string | null;
    }): Promise<Model> {
        return this.post('/api/models', model);
    }

    async updateModel(id: string, updates: {
        display_name?: string;
        provider_id?: string;
        vision_capable?: boolean;
        temperature?: number;
        max_tokens?: number;
        system_prompt?: string | null;
    }): Promise<Model> {
        return this.patch(`/api/models/${this.encodePathId(id)}`, updates);
    }

    async deleteModel(id: string): Promise<void> {
        await this.delete(`/api/models/${this.encodePathId(id)}`);
    }

    // Provider settings
    async listProviders(): Promise<Provider[]> {
        return this.get('/api/providers');
    }

    async createProvider(provider: Omit<Provider, 'id' | 'created_at'>): Promise<Provider> {
        return this.post('/api/providers', provider);
    }

    async updateProvider(id: string, updates: Partial<Omit<Provider, 'id' | 'created_at'>>): Promise<Provider> {
        return this.patch(`/api/providers/${encodeURIComponent(id)}`, updates);
    }

    async deleteProvider(id: string): Promise<void> {
        await this.delete(`/api/providers/${encodeURIComponent(id)}`);
    }

    // General settings
    async getSettings(): Promise<{ default_model_id: string }> {
        return this.get('/api/settings');
    }

    async updateSettings(settings: { default_model_id: string }): Promise<{ default_model_id: string }> {
        return this.patch('/api/settings', settings);
    }
}